import {
  type LpcFact,
  type LpcScope,
  type Range,
  type SppFact,
  type SppScope,
  VIEWS,
  chainName,
  distinct,
  matches,
  recentLpc,
  recentSpp,
  sppVisible,
  worstLpc,
  worstSpp,
} from './liveDataAdapter';
import { recentLaunchesSppScope, worstPerformersSppScope } from './worksheetScope';

/** Use the worksheet's own row population and range semantics, excluding just the requested member. */
export function marketplaceLocalOptions(
  view: string,
  lpc: LpcFact[],
  spp: SppFact[],
  lpcScope: LpcScope,
  sppScope: SppScope,
  today: string,
  recentDays: Range,
  recentSppDays: Range,
  views: Range,
  impressions: Range,
  worstChannel: SppScope['detail'],
) {
  const domain = (key: string): string[] => {
    const ls = { ...lpcScope, [key]: 'all' as const, action: null };
    const ss = { ...sppScope, [key]: 'all' as const, action: null };
    if (view === VIEWS[0]) {
      // The existing aggregation applies all four member predicates without the chart action.
      const facts = lpc.filter(
        (fact) =>
          matches(ls.product, fact.product) &&
          matches(ls.country, fact.country) &&
          matches(ls.chain, chainName(fact.parent)) &&
          matches(ls.vendor, fact.vendor),
      );
      return distinct(facts.map((fact) => (key === 'chain' ? chainName(fact.parent) : fact[key])));
    }
    if (view === VIEWS[1]) {
      const facts = spp.filter((fact) => sppVisible(fact, ss));
      return distinct(facts.map((fact) => (key === 'chain' ? chainName(fact.parent) : fact[key])));
    }
    if (view === VIEWS[2]) {
      const left = recentLpc(lpc, ls, today, recentDays);
      const right = recentSpp(spp, recentLaunchesSppScope(ss), today, recentSppDays);
      return distinct([
        ...(key === 'property' ? [] : left.map((row) => (key === 'chain' ? chainName(row.chain) : row[key]))),
        ...(key === 'vendor' ? [] : right.map((row) => (key === 'chain' ? chainName(row.chain) : row[key]))),
      ]);
    }
    const left = worstLpc(lpc, ls, views);
    const right = worstSpp(spp, worstPerformersSppScope(ss, key === 'detail' ? 'all' : worstChannel), impressions);
    if (key === 'country') return distinct([...left.map((row) => row.country), ...right.map((row) => row.country)]);
    // Detail is removed before the impressions range is evaluated at property grain.
    // Keep only facts belonging to a qualifying property/country/campaign group.
    const qualifying = new Set(
      right.map((row) => JSON.stringify([row.property, row.country, row.latestId, row.planned])),
    );
    return distinct(
      spp
        .filter((fact) => qualifying.has(JSON.stringify([fact.property, fact.country, fact.latestId, fact.planned])))
        .filter((fact) => sppVisible(fact, worstPerformersSppScope(ss, 'all')))
        .map((fact) => fact.detail),
    );
  };
  return Object.fromEntries(
    (view === VIEWS[0]
      ? ['product', 'country', 'chain', 'vendor']
      : view === VIEWS[1]
        ? ['country', 'channel', 'detail', 'chain', 'property']
        : view === VIEWS[2]
          ? ['country', 'chain', 'vendor', 'property']
          : ['country', 'detail']
    ).map((key) => [key, domain(key)]),
  );
}
